import { atom, Getter } from 'jotai';

import { bookingLoadsReceiveAtom } from '../_graphql/mutationBookingLoadsReceive';
import { bookingLoadsReconcileAtom } from '../_graphql/mutationBookingLoadsReconcile';
import { bookingMarkAsCompletedAtom } from '../_graphql/mutationBookingMarkAsCompleted';
import { bookingMarkAsOpenAtom } from '../_graphql/mutationBookingMarkAsOpen';
import { bookingMarkAsShippedAtom } from '../_graphql/mutationBookingMarkAsShipped';
import { bookingMarkAsVoidedAtom } from '../_graphql/mutationBookingMarkAsVoided';
import { bookingRevertStatusAtom } from '../_graphql/mutationBookingRevertStatus';
import { bookingUpdateAtom } from '../_graphql/mutationBookingUpdate';
import { bookingDetailQueryAtom } from '../_graphql/queryBookingDetail';
import { containerTypesQueryAtom } from '../_graphql/queryContainerTypes';
import { loadsListQueryAtom } from '../_graphql/queryLoadsList';
import { generateFileAccessURLMutationAtom } from '../hooks/useFileDownloadUrl';
import { tradeRoutePaths } from '../routes';
import { BookingDetailsTab } from '../types/booking';

import { atomFromQueryString, atomFromRoutePath } from '~/shared/state';

// 🟣🟣🟣 PARAMS - Booking Detail View
export const $bookingDetailsPathParams = atomFromRoutePath(
  tradeRoutePaths.bookingDetail,
);

export const $bookingId = atom((get) =>
  get($bookingDetailsPathParams)?.id
    ? String(get($bookingDetailsPathParams)?.id)
    : undefined,
);

export const $useDefaultPhotoRequirements = atom<boolean>(false);

// 🟢🟢🟢 REMOTE DATA - Booking Detail
export const $remoteBookingDetail = bookingDetailQueryAtom((get) => ({
  variables: { id: get($bookingId) ?? '' },
  skip: !get($bookingId),
}));

// 🟢🟢🟢 REMOTE DATA - Container Types
export const $containerTypes = containerTypesQueryAtom(() => ({
  variables: {},
  skip: false,
}));

// 🟢🟢🟢 REMOTE DATA - Booking Loads
export const $remoteBookingLoads = loadsListQueryAtom((get) => ({
  variables: {
    bookingId: get($bookingId),
  },
  skip: !get($bookingId),
}));

const refetchBookingDetail = (get: Getter) => {
  if (get($bookingId)) {
    get($remoteBookingDetail).refetch?.();
  }
};

// 🟠🟠🟠 ACTIONS - Booking Update
export const $updateBooking = bookingUpdateAtom({
  onSuccess: (get) => {
    refetchBookingDetail(get);
  },
});

// 🟠🟠🟠 ACTIONS - Booking Status Transitions
export const $markAsOpen = bookingMarkAsOpenAtom({
  onSuccess: (get) => {
    refetchBookingDetail(get);
  },
});

export const $markAsShipped = bookingMarkAsShippedAtom({
  onSuccess: (get) => {
    refetchBookingDetail(get);
  },
});

export const $markAsCompleted = bookingMarkAsCompletedAtom({
  onSuccess: (get) => {
    refetchBookingDetail(get);
  },
});

export const $markAsVoided = bookingMarkAsVoidedAtom({
  onSuccess: (get) => {
    refetchBookingDetail(get);
  },
});

export const $revertStatus = bookingRevertStatusAtom({
  onSuccess: (get) => {
    refetchBookingDetail(get);
  },
});

// 🟠🟠🟠 ACTION - Booking Bulk Receive All Loads
export const $receiveAllLoads = bookingLoadsReceiveAtom({
  onSuccess: (get) => {
    refetchBookingDetail(get);
    get($remoteBookingLoads).refetch?.();
  },
});

// 🟠🟠🟠 ACTION - Booking Bulk Reconcile All Loads
export const $reconcileAllLoads = bookingLoadsReconcileAtom({
  onSuccess: (get) => {
    refetchBookingDetail(get);
    get($remoteBookingLoads).refetch?.();
  },
});

// 🟠🟠🟠 ACTION -> GENERATE file access URL
export const $generateFileAccessURL = generateFileAccessURLMutationAtom();

// 🔘🔘🔘 PLAIN ATOMS - UI State
export const $tab = atomFromQueryString.string<BookingDetailsTab>('tab');
export const $isEditMode = atom<boolean>(false);
export const $hasUnsavedChanges = atom<boolean>(false);

// 🟢🟢🟢 COMPUTED DATA - Booking Data
export const $bookingData = atom((get) => {
  const data = get($remoteBookingDetail);
  return data?.data?.booking ?? null;
});

export const $isBookingLoading = atom((get) => {
  const data = get($remoteBookingDetail);
  return data?.isLoading ?? false;
});

export const $bookingError = atom((get) => {
  const data = get($remoteBookingDetail);
  return data?.error ?? null;
});

/**
 * Required fields for a non-draft booking based on
 * useValidateBookingInfoStep validation rules.
 */
export const BOOKING_REQUIRED_FIELDS = [
  'bookingNumber',
  'vesselName',
  'steamshipLine',
  'loadingPortId',
  'destinationPortId',
] as const;

export type BookingRequiredField = (typeof BOOKING_REQUIRED_FIELDS)[number];

type BookingFieldValidator<T> = (data: T) => boolean;

const bookingFieldValidators: Record<
  BookingRequiredField,
  BookingFieldValidator<{
    bookingNumber?: string | null;
    vesselName?: string | null;
    steamshipLine?: string | null;
    loadingPortId?: string | null;
    destinationPortId?: string | null;
  }>
> = {
  bookingNumber: (data) => !data.bookingNumber?.trim(),
  vesselName: (data) => !data.vesselName?.trim(),
  steamshipLine: (data) => !data.steamshipLine?.trim(),
  loadingPortId: (data) => !data.loadingPortId?.trim(),
  destinationPortId: (data) => !data.destinationPortId?.trim(),
};

const bookingFieldErrorMessages: Record<BookingRequiredField, string> = {
  bookingNumber: 'Booking number is required',
  vesselName: 'Vessel name is required',
  steamshipLine: 'Steamship line is required',
  loadingPortId: 'Port of loading is required',
  destinationPortId: 'Port of destination is required',
};

const getMissingBookingRequiredFields = (data: {
  bookingNumber?: string | null;
  vesselName?: string | null;
  steamshipLine?: string | null;
  loadingPortId?: string | null;
  destinationPortId?: string | null;
}): BookingRequiredField[] =>
  BOOKING_REQUIRED_FIELDS.filter((field) =>
    bookingFieldValidators[field](data),
  );

export const getBookingRequiredFieldErrors = (
  missingFields: BookingRequiredField[],
): Record<string, string> =>
  missingFields.reduce(
    (acc, field) => ({
      ...acc,
      [field]: bookingFieldErrorMessages[field],
    }),
    {} as Record<string, string>,
  );

// 🟢🟢🟢 COMPUTED DATA - Missing Required Fields
export const $bookingMissingRequiredFields = atom((get) => {
  const booking = get($bookingData);
  if (!booking)
    return BOOKING_REQUIRED_FIELDS as unknown as BookingRequiredField[];
  return getMissingBookingRequiredFields(booking);
});

// 🟢🟢🟢 COMPUTED DATA - Is Missing Required
export const $bookingIsMissingRequired = atom(
  (get) => get($bookingMissingRequiredFields).length > 0,
);
